import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import "mdb-react-ui-kit/dist/css/mdb.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./App.css";

// Auth Context
import { AuthProvider, useAuth } from "./context/AuthContext";

// App State Context (for page state persistence)
import { AppStateProvider } from "./context/AppStateContext";

import PosIndex from "./components/POS/pos_index";

// Auth Components
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";

// Services
import ServicePages from "./pages/ServicePage";

// Dashboard Layout
import DashboardLayout from "./components/Layout/DashboardLayout";

// Dashboard Pages
import DashboardPage from "./pages/DashboardPage";
import AddProductPage from "./pages/AddProductPage";
import ViewProductsPage from "./pages/ViewProductsPage";
import ProductDetailsPage from "./pages/ProductDetailsPage";
import ShopifyIntegrationPage from "./pages/ShopifyIntegrationPage";
import EbayIntegrationPage from "./pages/EbayIntegrationPage";
import PoshmarkIntegrationPage from "./pages/PoshmarkIntegrationPage";
import EbaySettingsPage from "./pages/EbaySettingsPage";
import ShopifySettingsPage from "./pages/ShopifySettingsPage";
import GeneralSettingPage from "./pages/GeneralSettingPage";
import InviteUsersPage from "./pages/InviteUsersPage";
import SorterPage from "./pages/SorterPage";
import ChatbotPage from "./pages/ChatbotPage";

//policy privacy page
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";

// Google Client ID
const GOOGLE_CLIENT_ID =
  "611740572510-n9ctrclusl3tc1ja253f2ggfg7al7kna.apps.googleusercontent.com";

// =============================================
// PROTECTED ROUTE COMPONENT
// =============================================
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        Loading...
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  const featureRaw =
    user?.features ||
    user?.feature ||
    localStorage.getItem("user_feature") ||
    "";
  const featureKey = featureRaw
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  const isSmartScan = featureKey.includes("smartscan");
  const isSmartList = featureKey.includes("smartlist");
  const isAllInOne = featureKey.includes("all_in_one");

  if (isSmartScan && location.pathname !== "/sorter") {
    return <Navigate to="/sorter" replace />;
  }

  if (isSmartList && !isAllInOne && location.pathname === "/sorter") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// =============================================
// PUBLIC ROUTE COMPONENT (Allow access even when logged in)
// =============================================
const PublicRoute = ({ children }) => {
  return children;
};

// =============================================
// ROOT REDIRECT (Authenticated -> /dashboard)
// =============================================
const RootRedirect = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        Loading...
      </div>
    );
  }

  return isAuthenticated() ? (
    <Navigate to="/dashboard" replace />
  ) : (
    <Navigate to="/login" replace />
  );
};

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AppStateProvider>
        <AuthProvider>
          <Router>
            <div className="App">
              <Routes>
                {/* ============================================ */}
                {/* PUBLIC ROUTES (Login/Register Only) */}
                {/* ============================================ */}
                <Route path="/" element={<RootRedirect />} />

                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <LoginPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/privacy-policy"
                  element={
                    <PublicRoute>
                      <PrivacyPolicyPage />
                    </PublicRoute>
                  }
                />

                <Route
                  path="/register"
                  element={
                    <PublicRoute>
                      <RegisterPage />
                    </PublicRoute>
                  }
                />

                {/* ============================================ */}
                {/* PROTECTED ROUTES (Dashboard & All Pages) */}
                {/* ============================================ */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  {/* Service */}
                  <Route path="services" element={<ServicePages />} />
                  {/* Dashboard Home */}
                  <Route path="dashboard" element={<DashboardPage />} />
                  {/* POS Page */}
                  <Route path="pos" element={<PosIndex />} />

                  {/* Products */}
                  <Route path="products" element={<ViewProductsPage />} />
                  <Route path="products/add" element={<AddProductPage />} />

                  {/* NEW: View / Update Product */}
                  <Route path="products/:id" element={<ProductDetailsPage />} />

                  {/* Integrations */}
                  <Route
                    path="integrations/shopify"
                    element={<ShopifyIntegrationPage />}
                  />
                  <Route
                    path="integrations/ebay"
                    element={<EbayIntegrationPage />}
                  />
                  <Route
                    path="integrations/poshmark"
                    element={<PoshmarkIntegrationPage />}
                  />

                  {/* Settings */}
                  <Route path="settings/ebay" element={<EbaySettingsPage />} />
                  <Route
                    path="settings/shopify"
                    element={<ShopifySettingsPage />}
                  />
                  <Route
                    path="settings/general"
                    element={<GeneralSettingPage />}
                  />

                  {/* Invite Users */}
                  <Route path="invite-users" element={<InviteUsersPage />} />

                  {/* Sorter Tool */}
                  <Route path="sorter" element={<SorterPage />} />
                  {/* Chatbot Tool */}
                  <Route path="chatbot" element={<ChatbotPage />} />
                </Route>

                {/* ============================================ */}
                {/* 404 - CATCH ALL (Optional) */}
                {/* ============================================ */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
      </AppStateProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
